import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import './AdsTxtGenerator.css';

const AdsTxtGenerator = ({ domain, siteId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [pastedText, setPastedText] = useState('');
  const [usePastedText, setUsePastedText] = useState(false);
  const [autoFillCerts, setAutoFillCerts] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!usePastedText && domain) {
      generateOptimized();
    }
  }, [domain, autoFillCerts]); // eslint-disable-line react-hooks/exhaustive-deps -- run when domain or cert auto-fill changes only

  const generateOptimized = async () => {
    if (!domain && !pastedText) {
      setError('Please provide a domain or paste your ads.txt content');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setCopied(false);

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (domain) {
        params.append('domain', domain);
      }
      if (siteId) {
        params.append('siteId', siteId);
      }
      if (autoFillCerts) {
        params.append('autoFillCerts', 'true');
      }

      let response;
      if (usePastedText && pastedText) {
        // Support pasted text via POST
        response = await axios.post(
          `/api/compliance/ads-txt`,
          { domain, siteId, adsTxtRaw: pastedText, autoFillCerts },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.get(`/api/compliance/ads-txt?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.data.fetchFailed) {
        setUsePastedText(true);
        setError(response.data.message || 'Could not fetch ads.txt. Please paste your ads.txt content below.');
        return;
      }

      setResult(response.data);
    } catch (err) {
      console.error('Error generating optimized ads.txt:', err);
      if (err.response?.status === 404 || err.response?.data?.message?.includes('fetch')) {
        setUsePastedText(true);
        setError('Could not fetch ads.txt automatically. Please paste your ads.txt content below.');
      } else {
        setError(err.response?.data?.message || 'Failed to generate optimized ads.txt. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (result?.optimizedAdsTxt) {
      navigator.clipboard.writeText(result.optimizedAdsTxt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (result?.optimizedAdsTxt) {
      const blob = new Blob([result.optimizedAdsTxt], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ads.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="ads-txt-generator-modal">
      <div className="ads-txt-generator-content">
        <div className="ads-txt-generator-header">
          <h2>Generate Optimized ads.txt</h2>
          <button className="ads-txt-generator-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="ads-txt-generator-body">
          {!result && !loading && (
            <div className="ads-txt-generator-options">
              <div className="ads-txt-generator-option">
                <label>
                  <input
                    type="checkbox"
                    checked={autoFillCerts}
                    onChange={(e) => setAutoFillCerts(e.target.checked)}
                  />
                  <span>Auto-fill certification IDs</span>
                </label>
                <p className="option-description">
                  Automatically add known certification authority IDs for major ad networks
                </p>
              </div>

              {usePastedText && (
                <div className="ads-txt-generator-paste">
                  <label htmlFor="ads-txt-paste">Paste your ads.txt content:</label>
                  <textarea
                    id="ads-txt-paste"
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="google.com, pub-1234567890123456, DIRECT, f08c47fec0942fa0&#10;openx.com, 123456789, RESELLER, 6a698e2ec38604c6"
                    rows={8}
                  />
                </div>
              )}
            </div>
          )}

          {loading && (
            <div className="ads-txt-generator-loading">
              <div className="spinner" />
              <p>Generating optimized ads.txt...</p>
            </div>
          )}

          {error && (
            <div className="ads-txt-generator-error">
              <p>{error}</p>
              {usePastedText && (
                <button className="btn-generate" onClick={generateOptimized}>
                  Generate from Pasted Text
                </button>
              )}
            </div>
          )}

          {result && (
            <div className="ads-txt-generator-result">
              <div className="ads-txt-generator-summary">
                <h3>Optimization Summary</h3>
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-label">Direct:</span>
                    <span className="stat-value">{result.summary.directCount}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Indirect:</span>
                    <span className="stat-value">{result.summary.indirectCount}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Removed:</span>
                    <span className="stat-value">{result.summary.removedCount}</span>
                  </div>
                  {result.summary.addedCerts > 0 && (
                    <div className="stat">
                      <span className="stat-label">Cert IDs Added:</span>
                      <span className="stat-value">{result.summary.addedCerts}</span>
                    </div>
                  )}
                </div>
              </div>

              {result.warnings && result.warnings.length > 0 && (
                <div className="ads-txt-generator-warnings">
                  <h4>Warnings ({result.warnings.length})</h4>
                  <ul>
                    {result.warnings.slice(0, 10).map((warning, index) => (
                      <li key={index}>
                        <strong>{warning.reason}:</strong> {warning.rawLine || warning.line}
                        {warning.suggestion && <span> — {warning.suggestion}</span>}
                      </li>
                    ))}
                    {result.warnings.length > 10 && (
                      <li className="more-warnings">... and {result.warnings.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="ads-txt-generator-output">
                <div className="output-header">
                  <h4>Optimized ads.txt</h4>
                  <div className="output-actions">
                    <button
                      className="btn-copy"
                      onClick={handleCopy}
                      disabled={copied}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button className="btn-download" onClick={handleDownload}>
                      Download
                    </button>
                  </div>
                </div>
                <textarea
                  readOnly
                  value={result.optimizedAdsTxt}
                  className="ads-txt-output"
                  rows={20}
                />
              </div>

              <div className="ads-txt-generator-instructions">
                <h4>Next Steps</h4>
                <ol>
                  <li>Review the optimized ads.txt above</li>
                  <li>Copy or download the file</li>
                  <li>Upload it to your server at <code>https://{domain}/ads.txt</code></li>
                  <li>Verify it's accessible at that URL</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        <div className="ads-txt-generator-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {result && (
            <button className="btn-primary" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

AdsTxtGenerator.propTypes = {
  domain: PropTypes.string.isRequired,
  siteId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default AdsTxtGenerator;

